// useRolesConfig.js - 动态获取角色配置
// 🆕 架构升级：从GoMAown2 API动态获取角色配置，替代硬编码
// 🆕 多语言支持：API同时返回中英文角色名，合并后供前端匹配

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GOMAOWN_API_URL } from '../config/serviceConfig';

// 默认配置（API不可用时的回退）
const DEFAULT_CONFIG = {
  teamRoles: [
    "产品经理", "架构师", "工程师", "QA工程师", "项目经理", "UI工程师",
    "硬件架构师", "硬件工程师", "硬件制造工程师",
    "产品经理导师", "架构师导师", "工程师导师"
  ],
  teamRolesEN: [],
  mentorRoleMap: {
    "产品经理导师": "产品经理",
    "架构师导师": "架构师",
    "工程师导师": "工程师"
  },
  mentorRoleMapEN: {}
};

const buildRolesConfigUrls = (apiBase) => {
  const base = (apiBase || GOMAOWN_API_URL).replace(/\/$/, '');
  const urls = [`${base}/api/config/roles`];

  try {
    const parsed = new URL(base, window.location.href);
    if (parsed.pathname === '/' || parsed.pathname === '') {
      urls.push(`${base}/api/ccma/api/config/roles`);
    }
  } catch (e) {
    // 保持主请求路径；URL 解析失败通常只会出现在非常规测试环境。
  }

  return [...new Set(urls)];
};

/**
 * 获取角色配置的Hook
 * 合并中英文角色名，确保无论当前语言是什么，消息中的agentName都能被匹配到
 * @returns {Object} { teamRoles: string[], mentorRoleMap: object, isLoading: boolean, error: Error|null }
 */
export function useRolesConfig(apiUrl) {
  const API_BASE = apiUrl || GOMAOWN_API_URL;
  const [rawConfig, setRawConfig] = useState(DEFAULT_CONFIG);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { i18n } = useTranslation();

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setIsLoading(true);
        const urls = buildRolesConfigUrls(API_BASE);
        let result = null;
        let lastError = null;

        for (const url of urls) {
          try {
            const response = await fetch(url);
            if (!response.ok) {
              lastError = new Error(`HTTP error! status: ${response.status}`);
              continue;
            }
            const json = await response.json();
            if (json.code === 0 && json.data) {
              result = json;
              break;
            }
            lastError = new Error(json.message || '获取配置失败');
          } catch (err) {
            lastError = err;
          }
        }
        
        if (!result) {
          throw lastError || new Error('获取配置失败');
        }
        
        if (result.code === 0 && result.data) {
          setRawConfig({
            teamRoles: result.data.teamRoles || DEFAULT_CONFIG.teamRoles,
            teamRolesEN: result.data.teamRolesEN || [],
            mentorRoleMap: result.data.mentorRoleMap || DEFAULT_CONFIG.mentorRoleMap,
            mentorRoleMapEN: result.data.mentorRoleMapEN || {}
          });
          console.log('[useRolesConfig] 成功加载角色配置:', 
            result.data.teamRoles?.length, '个角色(中文),',
            result.data.teamRolesEN?.length || 0, '个角色(英文),',
            Object.keys(result.data.mentorRoleMap || {}).length, '个导师映射(中文),',
            Object.keys(result.data.mentorRoleMapEN || {}).length, '个导师映射(英文)');
        } else {
          throw new Error(result.message || '获取配置失败');
        }
      } catch (err) {
        console.warn('[useRolesConfig] 获取角色配置失败，使用默认配置:', err.message);
        setError(err);
        // 保持使用默认配置
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, [API_BASE]);

  // 合并中英文角色名：teamRoles 包含中文+英文所有角色名，确保消息中的agentName都能匹配
  const mergedConfig = useMemo(() => {
    // 合并 teamRoles：中文 + 英文去重
    const allRoles = [...rawConfig.teamRoles];
    if (rawConfig.teamRolesEN && rawConfig.teamRolesEN.length > 0) {
      const existingSet = new Set(allRoles);
      for (const enRole of rawConfig.teamRolesEN) {
        if (enRole && !existingSet.has(enRole)) {
          allRoles.push(enRole);
        }
      }
    }

    // 合并 mentorRoleMap：中文映射 + 英文映射
    const allMentorMap = { ...rawConfig.mentorRoleMap };
    if (rawConfig.mentorRoleMapEN) {
      Object.assign(allMentorMap, rawConfig.mentorRoleMapEN);
    }

    return {
      teamRoles: allRoles,
      mentorRoleMap: allMentorMap
    };
  }, [rawConfig]);

  return {
    teamRoles: mergedConfig.teamRoles,
    mentorRoleMap: mergedConfig.mentorRoleMap,
    isLoading,
    error
  };
}

export default useRolesConfig;

